import React from 'react';

// ─────────────────────────────────────────────
// 계약 정보 입력 폼 (공용)
//  - ContractEdit(수정) / ContractUpload(등록 전 검토) 양쪽에서 사용
//  - value : DB 형식 객체 (contract_type, landlord_info[], amount_info ...)
//  - onChange : 변경된 전체 객체를 돌려준다
//  - showOurSide : 공동중개 담당측 선택 노출 여부
// ─────────────────────────────────────────────

// 객체/배열 혼재 대응 → 항상 배열
export const toArray = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'object' && Object.keys(v).length === 0) return [];
  return [v];
};

// 날짜에서 시간 제거
export const fmtDate = (d) => (d ? String(d).split('T')[0] : '');

// 천단위 콤마
export const fmtMoney = (n) => (Number(n) || 0).toLocaleString('ko-KR');

// 계약금/중도금/잔금만 날짜 입력
export const needDate = (desc = '') => /계약금|중도금|잔금/.test(desc);

// payments 합계 (= 매매금액)
export const sumPayments = (payments = []) =>
  payments.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);

// 보증금·월차임은 별도 입력칸에서 관리하므로 목록에서 제외
export const isDupRentItem = (desc = '') =>
  /보증금|월세|차임/.test(desc) && !/현보증금|승계/.test(desc);

// 전화번호 자동 하이픈
export const fmtPhone = (v = '') => {
  const n = String(v).replace(/[^0-9]/g, '').slice(0, 11);
  if (!n) return '';
  if (n.startsWith('02')) {
    if (n.length <= 2) return n;
    if (n.length <= 5) return `${n.slice(0, 2)}-${n.slice(2)}`;
    if (n.length <= 9) return `${n.slice(0, 2)}-${n.slice(2, 5)}-${n.slice(5)}`;
    return `${n.slice(0, 2)}-${n.slice(2, 6)}-${n.slice(6, 10)}`;
  }
  if (n.length <= 3) return n;
  if (n.length <= 7) return `${n.slice(0, 3)}-${n.slice(3)}`;
  if (n.length <= 10) return `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}`;
  return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
};

const inputStyle = {
  width: '100%', padding: '8px',
  border: '1px solid #e2e8f0', borderRadius: '4px',
  fontSize: '13px', boxSizing: 'border-box',
};

const cardStyle = {
  marginBottom: '15px', padding: '10px',
  backgroundColor: '#fff', borderRadius: '4px',
};

const labelStyle = { display: 'block', marginBottom: '8px', fontWeight: 'bold' };

export default function ContractForm({ value, onChange, showOurSide = false }) {
  const c = value || {};
  const set = (patch) => onChange({ ...c, ...patch });

  const isSale = c.contract_type === 'SALE';
  const isRent = c.contract_type === 'RENT_TENANT';

  const brokerCount = toArray(c.broker_info).length;
  const isCoBroker = brokerCount > 1;

  // ─── payments 조작 ───
  const payments = c.amount_info?.payments || [];
  const setPayments = (next) =>
    set({ amount_info: { ...c.amount_info, payments: next } });

  const updatePayment = (idx, field, v) => {
    const next = [...payments];
    next[idx] = { ...next[idx], [field]: v };
    setPayments(next);
  };

  return (
    <div>
      {/* ─── 계약 유형 ─── */}
      <div style={cardStyle}>
        <label style={labelStyle}>📌 계약 유형:</label>
        <select
          value={c.contract_type || ''}
          onChange={(e) => set({ contract_type: e.target.value })}
          style={inputStyle}
        >
          <option value="">선택하세요</option>
          <option value="SALE">🏠 매매</option>
          <option value="RENT_TENANT">🔑 임차</option>
        </select>
      </div>

      {/* ─── 소재지 ─── */}
      <div style={cardStyle}>
        <label style={labelStyle}>📍 소재지:</label>
        <input
          type="text"
          placeholder="전체 주소"
          value={c.location_full_address || ''}
          onChange={(e) => set({ location_full_address: e.target.value })}
          style={inputStyle}
        />
      </div>

      {/* ─── 계약 날짜 ─── */}
      <div style={cardStyle}>
        <label style={labelStyle}>📅 계약 날짜:</label>
        <input
          type="date"
          value={fmtDate(c.contract_date)}
          onChange={(e) => set({ contract_date: e.target.value })}
          style={inputStyle}
        />
      </div>

      {/* ─── 임차 기간 (임차만) ─── */}
      {isRent && (
        <div style={cardStyle}>
          <label style={labelStyle}>📅 임차 기간:</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>임차 시작일</div>
              <input
                type="date"
                value={fmtDate(c.contract_start_date)}
                onChange={(e) => set({ contract_start_date: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>임차 종료일</div>
              <input
                type="date"
                value={fmtDate(c.contract_end_date)}
                onChange={(e) => set({ contract_end_date: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>
        </div>
      )}

      {/* ─── 당사자 (추가/삭제 가능) ─── */}
      {[
        { key: 'landlord_info', label: isSale ? '매도인' : '임대인', icon: '👤', isBroker: false },
        { key: 'tenant_info',   label: isSale ? '매수인' : '임차인', icon: '👤', isBroker: false },
        { key: 'broker_info',   label: '중개사무소',                icon: '🤝', isBroker: true  },
      ].map(({ key, label, icon, isBroker }) => {
        const list = toArray(c[key]);

        const updateField = (idx, field, v) => {
          const next = [...list];
          next[idx] = { ...next[idx], [field]: v };
          set({ [key]: next });
        };
        const addRow = () => {
          const blank = isBroker
            ? { name: '', phone: '', registrationNo: '', address: '' }
            : { role: '', name: '', phone: '', address: '' };
          set({ [key]: [...list, blank] });
        };
        const removeRow = (idx) => set({ [key]: list.filter((_, i) => i !== idx) });

        return (
          <div key={key} style={cardStyle}>
            <label style={labelStyle}>{icon} {label}:</label>

            {list.map((p, i) => (
              <div key={i} style={{
                marginBottom: '10px', padding: '10px',
                backgroundColor: '#fafafa', borderRadius: '4px', border: '1px solid #eee',
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isBroker ? '1.5fr 1fr 36px' : '0.8fr 1.2fr 1fr 36px',
                  gap: '6px', marginBottom: '6px',
                }}>
                  {!isBroker && (
                    <input
                      type="text" placeholder="관계"
                      value={p.role || ''}
                      onChange={(e) => updateField(i, 'role', e.target.value)}
                      style={inputStyle}
                    />
                  )}
                  <input
                    type="text" placeholder={isBroker ? '중개사무소 명칭' : '성명'}
                    value={p.name || ''}
                    onChange={(e) => updateField(i, 'name', e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="text" placeholder="연락처"
                    value={fmtPhone(p.phone)}
                    onChange={(e) => updateField(i, 'phone', fmtPhone(e.target.value))}
                    style={inputStyle}
                  />
                  <button
                    onClick={() => removeRow(i)} title="이 항목 삭제"
                    style={{
                      backgroundColor: '#f44336', color: 'white', border: 'none',
                      borderRadius: '4px', cursor: 'pointer',
                    }}
                  >
                    ✕
                  </button>
                </div>

                {isBroker ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '6px' }}>
                    <input
                      type="text" placeholder="등록번호"
                      value={p.registrationNo || ''}
                      onChange={(e) => updateField(i, 'registrationNo', e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      type="text" placeholder="사무소 소재지"
                      value={p.address || ''}
                      onChange={(e) => updateField(i, 'address', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                ) : (
                  <input
                    type="text" placeholder="주소"
                    value={p.address || ''}
                    onChange={(e) => updateField(i, 'address', e.target.value)}
                    style={inputStyle}
                  />
                )}
              </div>
            ))}

            <button
              onClick={addRow}
              style={{
                backgroundColor: '#2196F3', color: 'white', border: 'none',
                padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
              }}
            >
              ➕ {label} 추가
            </button>
          </div>
        );
      })}

      {/* ─── 공동중개 담당측 ─── */}
      {showOurSide && isCoBroker && (
        <div style={{ ...cardStyle, backgroundColor: '#fff3e0' }}>
          <label style={labelStyle}>🤝 공동중개 ({brokerCount}곳) — 우리 담당측:</label>
          <div style={{ fontSize: '12px', color: '#e65100', marginBottom: '6px' }}>
            담당측을 지정해야 문자가 발송됩니다. 잘못 지정하면 상대측에 문자가 갈 수 있으니 확인하세요.
          </div>
          <select
            value={c.our_side || 'unset'}
            onChange={(e) => set({ our_side: e.target.value })}
            style={inputStyle}
          >
            <option value="unset">선택하세요 (발송 보류)</option>
            <option value="landlord">임대(매도)측 담당</option>
            <option value="tenant">임차(매수)측 담당</option>
            <option value="both">양측 모두</option>
          </select>
        </div>
      )}

      {/* ─── 금액 정보 ─── */}
      <div style={cardStyle}>
        <label style={labelStyle}>💰 금액 정보:</label>

        {isSale && (
          <div style={{
            marginBottom: '12px', padding: '10px', backgroundColor: '#e3f2fd',
            borderRadius: '4px', display: 'flex', justifyContent: 'space-between',
          }}>
            <strong>매매금액 (자동합계)</strong>
            <strong>{fmtMoney(sumPayments(payments))} 원</strong>
          </div>
        )}

        {isRent && (
          <div style={{
            marginBottom: '12px', padding: '10px', backgroundColor: '#e8f5e9',
            borderRadius: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>보증금</div>
              <input
                type="number"
                value={c.amount_info?.depositAmount || ''}
                onChange={(e) => set({
                  amount_info: { ...c.amount_info, depositAmount: parseInt(e.target.value) || 0 },
                })}
                style={inputStyle}
              />
            </div>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>월세 (없으면 0)</div>
              <input
                type="number"
                value={c.amount_info?.monthlyRent || ''}
                onChange={(e) => set({
                  amount_info: { ...c.amount_info, monthlyRent: parseInt(e.target.value) || 0 },
                })}
                style={inputStyle}
              />
            </div>
          </div>
        )}

        <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
          항목 / 금액 / 날짜 (계약금·중도금·잔금만 날짜 입력)
        </div>

        {payments.map((p, i) => {
          if (isRent && isDupRentItem(p.description)) return null;
          return (
            <div key={i} style={{
              marginBottom: '8px', display: 'grid',
              gridTemplateColumns: '1.2fr 1fr 1fr 40px', gap: '6px', alignItems: 'center',
            }}>
              <input
                type="text" placeholder="계약금/중도금1/현보증금/잔금"
                value={p.description || ''}
                onChange={(e) => updatePayment(i, 'description', e.target.value)}
                style={inputStyle}
              />
              <input
                type="number" placeholder="금액"
                value={p.amount || ''}
                onChange={(e) => updatePayment(i, 'amount', parseInt(e.target.value) || 0)}
                style={inputStyle}
              />
              {needDate(p.description) ? (
                <input
                  type="date"
                  value={fmtDate(p.date)}
                  onChange={(e) => updatePayment(i, 'date', e.target.value)}
                  style={inputStyle}
                />
              ) : (
                <span style={{ fontSize: '11px', color: '#aaa', textAlign: 'center' }}>날짜 없음</span>
              )}
              <button
                onClick={() => setPayments(payments.filter((_, idx) => idx !== i))}
                title="이 항목 삭제"
                style={{
                  backgroundColor: '#f44336', color: 'white', border: 'none',
                  borderRadius: '4px', cursor: 'pointer', padding: '8px',
                }}
              >
                ✕
              </button>
            </div>
          );
        })}

        <button
          onClick={() => setPayments([...payments, { description: '', amount: 0, date: '' }])}
          style={{
            backgroundColor: '#2196F3', color: 'white', border: 'none',
            padding: '6px 12px', borderRadius: '4px', cursor: 'pointer',
            fontSize: '12px', marginTop: '4px',
          }}
        >
          ➕ 항목 추가
        </button>
      </div>

      {/* ─── 특약 ─── */}
      <div style={cardStyle}>
        <label style={labelStyle}>⚠️ 특약:</label>
        <textarea
          value={c.special_clauses || ''}
          onChange={(e) => set({ special_clauses: e.target.value })}
          style={{ ...inputStyle, minHeight: '120px', fontFamily: 'Arial, sans-serif' }}
        />
      </div>
    </div>
  );
}
